import AppLayout from '@/components/AppLayout';
import { Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App, ConfigProvider } from 'antd';
import { DebugProvider } from '@/libs/debug/DebugContext';
import {
	ReactNode,
	createContext,
	useCallback,
	useContext,
	// useEffect,
	useState,
} from 'react';
import themeConfig from './libs/antd/theme';
import { getCookie } from './utils/cookie';
import MapLoader from './components/MapLoader';
import * as Sentry from '@sentry/react';
import type { Scope } from '@sentry/react';

// PAGE IMPORTS
// import FBDHCustomerCheckingPage from '@/pages/customer-checking/fbdh/CustomerCheckingPage';

import CustomerListPage from '@/pages/frm/customer/CustomerListPage';
// import CustomerPageV1 from '@/pages/frm/customer/CustomerPage';
import CustomerPageV2 from '@/pages/frm/customer-v2/CustomerPage';

import FRMCustomerCheckingListPage from '@/pages/frm/customer-checking/CustomerCheckingListPage';
import FRMCustomerCheckingPage from '@/pages/frm/customer-checking/CustomerCheckingPage';

import FRMCustomerGroupListPage from '@/pages/frm/customer-group/CustomerGroupListPage';
import CreateCustomerGroupPage from '@/pages/frm/customer-group/CreateCustomerGroupPage';
import EditCustomerGroupPage from '@/pages/frm/customer-group/EditCustomerGroupPage';
import CRCustomerGroupListPage from '@/pages/cr/customer-group/CustomerGroupListPage';

import DashboardPage from '@/pages/dashboard/DashboardPage';
import LoginPage from '@/pages/login/LoginPage';
import NotFoundPage from '@/pages/NotFound';

// FBDH
import FBDHCustomerGroupListPage from '@/pages/fbdh/customer-group/CustomerGroupListPage';
import FBDHProspectListPage from '@/pages/fbdh/prospect/ProspectListPage';
import FBDHCustomerCheckingListPage from '@/pages/fbdh/customer-checking/CustomerCheckingListPage';
import FBDHCustomerCheckingPage from '@/pages/fbdh/customer-checking/CustomerCheckingPage';
import FBDHRequestSurveyListPage from './pages/fbdh/survey/request-survey/RequestSurveyListPage';
import FBDHRequestSurveyPage from './pages/fbdh/survey/request-survey/RequestSurveyPage';
import FBDHVisitReportListPage from './pages/fbdh/visit-report/VisitReportListPage';
import FBDHVisitReportApproval from './pages/fbdh/visit-report/VisitReportApproval';
import FBDHVisitReportRejectListPage from './pages/fbdh/visit-report/VisitReportRejectListPage';
import FBDHVisitReportRejectPage from './pages/fbdh/visit-report/VisitReportRejectPage';

import FRMProspectPage from '@/pages/frm/prospect/ProspectPage';
import CCAProspectListPage from '@/pages/cca/prospect/ProspectListPage';

import FRMProspectListPage from '@/pages/frm/prospect/ProspectListPage';
import AccountPage from './pages/account/AccountPage';
import CCARequestSLIKPage from '@/pages/cca/customer-checking/RequestSLIKPage';
import CCARequestSLIKForm from './pages/cca/customer-checking/RequestSLIKForm';
import CCAAnalisaSLIKPage from './pages/cca/customer-checking/AnalisaSLIKPage';
import CCAAnalisaSLIKForm from './pages/cca/customer-checking/AnalisaSLIKForm';
import CCASurveyVerificationListPage from './pages/cca/survey/survey-verification/SurveyVerificationListPage';
import CCASurveyVerificationPage from './pages/cca/survey/survey-verification/SurveyVerificationPage';
import DocPage from './pages/docs/DocPage';
import GroupMenuPage from './pages/group-access/GroupMenuPage';
import MasterGroupPage from './pages/group-access/MasterGroupPage';
import MasterMenuPage from './pages/group-access/MasterMenuPage';
import UserGroupPage from './pages/group-access/UserGroupPage';

import CCDHProspectListPage from '@/pages/ccdh/prospect/ProspectListPage';
import CCDHSurveyApprovalListPage from './pages/ccdh/survey/survey-approval/SurveyApprovalListPage';
import CCDHSurveyApprovalPages from './pages/ccdh/survey/survey-approval/SurveyApprovalPage';
import CCDHAssignCCAListPage from './pages/ccdh/customer-checking/AssignCCAListPage';
import CCDHAssignCCAPage from './pages/ccdh/customer-checking/AssignCCAPage';
import CCDHAnalisaSLIKListPage from './pages/ccdh/customer-checking/AnalisaSLIKListPage';
import CCDHAnalisaSLIKPage from './pages/ccdh/customer-checking/AnalisaSLIKPage';
import CCDHVisitReportListPage from './pages/ccdh/visit-report/VisitReportListPage';
import CCDHVisitReportApproval from './pages/ccdh/visit-report/VisitReportApproval';

import GroupAccessForm from './pages/settings/GroupAccessForm';
import GroupAccessPage from './pages/settings/GroupAccessPage';

import FRMPraSurveyInputPage from './pages/frm/survey/pra-survey/PraSurveyInputPage';
import FRMPraSurveyPage from './pages/frm/survey/pra-survey/PraSurveyPage';
// import FRMPraSurveyForm from './pages/frm/survey/pra-survey/PraSurveyForm';
import FRMRequestSurveyPage from './pages/frm/survey/request-survey/RequestSurveyPage';
import FRMRequestSurveyForm from './pages/frm/survey/request-survey/RequestSurveyForm';
import FHSurveyApprovalListPage from './pages/fh/survey/survey-approval/SurveyApprovalListPage';
import FHSurveyApprovalPage from './pages/fh/survey/survey-approval/SurveyApprovalPage';
import CHSurveyApprovalListPage from './pages/ch/survey/survey-approval/SurveyApprovalListPage';
import CHSurveyApprovalPage from './pages/ch/survey/survey-approval/SurveyApprovalPage';

import FRMVisitReportListPage from './pages/frm/visit-report/VisitReportListPage';
import FRMVisitReportPage from './pages/frm/visit-report/office/VisitReportOfficePage';
import FRMVisitReportRejectPage from './pages/frm/visit-report/VisitReportRejectPage';
import FRMSiteVisitReportPage from './pages/frm/visit-report/site/SiteReportPage';
import FRMCallReportPage from './pages/frm/call-report/CallReportPage';
import FRMCallReportListPage from './pages/frm/call-report/CallReportListPage';
import FRMCallReportHistoryPage from './pages/frm/call-report/CallReportHistoryPage';

import FHVisitReportListPage from './pages/fh/visit-report/VisitReportListPage';
import FHVisitReportApproval from './pages/fh/visit-report/VisitReportApproval';

import CHVisitReportListPage from './pages/ch/visit-report/VisitReportListPage';
import CHVisitReportApproval from './pages/ch/visit-report/VisitReportApproval';

// static pages for demo and logic to be implemented later.
import ModalProvider from './components/modals/ModalProvider';
// import CCAPraSurveyPage from './pages/static/cca/pra-survey/PraSurveyPage';
// import CCAPraSurveyDetail from './pages/static/cca/pra-survey/PraSurveyPage.Detail';
// import CCASurveyDetail from './pages/static/cca/survey/SurveyDetailForm';
// import CCASurveyPage from './pages/static/cca/survey/SurveyPage';
// import CCDHSurveyApprovalDetail from './pages/static/ccdh/survey/SurveyApprovalDetail';
// import CCDHSurveyApprovalPage from './pages/static/ccdh/survey/SurveyApprovalPage';
// import StaticFBDHSurveyDetailForm from './pages/static/fbdh/survey/SurveyDetailForm';
// import StaticFBDHSurveyPage from './pages/static/fbdh/survey/SurveyPage';
// import StaticFRMPraSurveyForm from './pages/static/frm/pra-survey/PraSurveyForm';
// import FRMSurveyDetailForm from './pages/static/frm/survey/SurveyDetailForm';
// import FRMSurveyPage from './pages/static/frm/survey/SurveyPage';
// import KadivFleetApprovalPage from './pages/static/kadiv-fleet/ApprovalPage';
// import KadivFleetApprovalDetail from './pages/static/kadiv-fleet/ApprovalDetail';
// import KadivCreditApprovalDetail from './pages/static/kadiv-credit/ApprovalDetail';
// import KadivCreditApprovalPage from './pages/static/kadiv-credit/ApprovalPage';

import CCAVisitReportListPage from './pages/cca/visit-report/VisitReportListPage';
import CCAVisitReportVerificationPage from './pages/cca/visit-report/VisitReportVerificationPage';
import FRMPipelineListPage from './pages/frm/pipeline/PipelineListPage';
// import FRMPipelineListPageJF from './pages/frm/pipeline/PipelineListPageJF';
// import FRMPipelineListPageNonJF from './pages/frm/pipeline/PipelineListPageNonJF';
import FRMPipelinePage from './pages/frm/pipeline/PipelinePage';
import FBDHPipelineListPage from './pages/fbdh/pipeline/PipelineListPage';

import FBDHPipelinePage from './pages/fbdh/pipeline/PipelinePage';
import CCAPipelineListPage from './pages/cca/pipeline/PipelineListPage';
import CCAPipelinePage from './pages/cca/pipeline/PipelinePage';
import CCDHPipelineListPage from './pages/ccdh/pipeline/PipelineListPage';
import CCDHPipelinePage from './pages/ccdh/pipeline/PipelinePage';
import FHPipelineListPage from './pages/fh/pipeline/PipelineListPage';
import CHPipelineListPage from './pages/ch/pipeline/PipelineListPage';
import CCDHPipelineSummaryPage from './pages/ccdh/pipeline/PipelineSummaryPage';

import NAPListPage from './pages/nap/NAPListPage';
import NAPDownloadPage from './pages/nap/NAPDownloadPage';
import NAPJFDownloadPage from './pages/nap/NAPJFDownloadPage';
import NAPPage from './pages/nap/NAPPage';

import GoliveListPage from './pages/golive/GoliveListPage';
import GolivePage from './pages/golive/GolivePage';
import MonitoringPipelineGolivePage from './pages/monitoring/pipeline-golive/MonitoringPipelineGolivePage';

import FSCustomerListPage from './pages/fs/customer/CustomerListPage';
import FSCustomerPage from './pages/fs/customer/CustomerPage';

import SCRisalahListPage from './pages/sc/risalah/RisalahListPage';
import SCRisalahPage from './pages/sc/risalah/RisalahPage';

import OCRListPage from './pages/ocr/OCRListPage';
import OCRPage from './pages/ocr/OCRPage';
import OCRPageDetail from './pages/ocr/OCRPage.Detail';
import OCRCreate from './pages/ocr/OCRCreate';

import SPFListPage from './pages/spf/SPFListPage';
import SPFPage from './pages/spf/SPFPage';

import MKLListPage from './pages/mkl/MKLListPage';
import MKLPage from './pages/mkl/MKLPage';
import MKLHistorySPFPage from './pages/mkl/MKLHistorySPFPage';

// Robotic
import QCNewCustomerListPage from './pages/robotic/qc-new-customer/QCNewCustomerListPage';
import QCNewCustomerPage from './pages/robotic/qc-new-customer/QCNewCustomerPage';
import QCPraPencairanListPage from './pages/robotic/qc-pra-pencairan/QCPraPencairanListPage';
import QCPraPencairanPage from './pages/robotic/qc-pra-pencairan/QCPraPencairanPage';
import ActivityRoboticPraPencairanListPage from './pages/robotic/activity/ActivityRoboticPraPencairanListPage';
import ActivityRoboticDetailPage from './pages/robotic/activity/ActivityRoboticDetailPage';

import MPPListPage from './pages/mpp/MppListPage';
import MPPPage from './pages/mpp/MPPPage';
import HistorySLIKMPPPage from './pages/mpp/HistorySLIKMPPPage';
import CreateMPPListPage from './pages/mpp/CreateMPPListPage';
import HistoryMPPListPage from './pages/mpp/HistoryMPPListPage';
import HistorySLIKMPPListPage from './pages/mpp/HistorySLIKMPPListPage';

import PrintListPage from './pages/print/PrintListPage';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUserListPage from './pages/admin/AdminUserListPage';
import AdminEmailLogsPage from './pages/admin/AdminEmailLogsPage';
import AdminBouwheerListPage from './pages/admin/bouwheer/BouwheerListPage';
import AdminOrgStructurePage from './pages/admin/org-structure/OrgStructurePage';
import AdminIndustrySectorListPage from './pages/admin/industry-sector/IndustrySectorListPage';
import AdminCustomerListPage from './pages/admin/customer/AdminCustomerListPage';

import DMSListPage from './pages/dms/DMSListPage';
import DMSDetailPage from './pages/dms/DMSDetailPage';

// Monitoring Document
import MonitoringDocumentListPage from './pages/monitoring/document/MonitoringDocumentListPage';
import MonitoringDocumentDetailPage from './pages/monitoring/document/MonitoringDocumentDetailPage';
import PrintPage from './pages/print/PrintPage';
import DisbursementListPage from './pages/print/disbursement/DisbursementListPage';

// FCS
import PlakatListPage from './pages/fcs/plakat/PlakatListPage';
import DisbursementPOListPage from './pages/print/disbursement/DisbursementPOListPage';
import DisbursementPage from './pages/print/disbursement/DisbursementPage';
import DisbursementPOHistory from './pages/print/disbursement/DisbursementPOHistory';

import VerifyDisbursementListPage from './pages/print/verification/VerificationListPage';
import VerifyDisbursementPOListPage from './pages/print/verification/VerificationListPOListPage';
import VerifyDisbursementPage from './pages/print/verification/VerificationPage';
import VerificationPOHistory from './pages/print/verification/VerificationPOHistory';

// monitoring
import MonitoringSLAListPage from './pages/monitoring/sla/MonitoringSLAListPage';
import MonitoringSLAPage from './pages/monitoring/sla/MonitoringSLAPage';
import MonitoringPlafondListPage from './pages/monitoring/plafond/MonitoringPlafondListPage';
import MonitoringPlafondPage from './pages/monitoring/plafond/MonitoringPlafondPage';
import MonitoringPlafondAgreement from './pages/monitoring/plafond/MonitoringPlafondAgreement';
import MonitoringPlafondPO from './pages/monitoring/plafond/MonitoringPlafondPO';
import MonitoringCovenantListPage from './pages/monitoring/covenant/MonitoringCovenantListPage';
import MonitoringCovenantPage from './pages/monitoring/covenant/MonitoringCovenantPage';
import MonitoringCovenantCustomerPage from './pages/monitoring/covenant/MonitoringCovenantCustomerPage';

// TODO: Create SimulationPage component in ./pages/frm/simulation/SimulationPage.tsx
import FRMSimulationListPage from './pages/frm/simulation/SimulationListPage';
import FRMSimulationPage from './pages/frm/simulation/SimulationPage';
import SimulationFacilityListPage from './pages/frm/simulation/SimulationFacilityListPage';
import FRMSimulationHistoryListPage from './pages/frm/simulation/SimulationHistoryListPage';
import SimulationPage from './pages/frm/simulation/SimulationPage';
import SimulationPagePreview from './pages/frm/simulation/SimulationPagePreview';
import FRMPOListPage from './pages/frm/purchase-order/POListPage';
import FRMPODetailListPage from './pages/frm/purchase-order/PODetailListPage';
import FRMPOActionListPage from './pages/frm/purchase-order/POActionListPage';
import FBDHPOListPage from './pages/fbdh/purchase-order/POListPage';
import FBDHPODetailListPage from './pages/fbdh/purchase-order/PODetailListPage';
import FBDHPOApprovalListPage from './pages/fbdh/purchase-order/POApprovalListPage';
import FCASPOListPage from './pages/fcas/purchase-order/POListPage';

export const PAGE_COMPONENT = {
	NotFoundPage,
	DocPage,
	LoginPage,
	DashboardPage,
	CustomerListPage,
	FRMCustomerGroupListPage,
	CreateCustomerGroupPage,
	EditCustomerGroupPage,
	CRCustomerGroupListPage,

	FRMCustomerCheckingListPage,
	// FRMCustomerCheckingDetailPage,
	FRMCustomerCheckingPage,

	CustomerPage: CustomerPageV2,
	FRMPraSurveyInputPage,
	FRMProspectPage,
	FRMProspectListPage,
	FRMPipelineListPage,
	// FRMPipelineListPageJF,
	// FRMPipelineListPageNonJF,
	FRMPipelinePage,

	// FBDH
	FBDHCustomerGroupListPage,
	FBDHProspectListPage,
	FBDHCustomerCheckingListPage,
	FBDHCustomerCheckingPage,
	FBDHRequestSurveyListPage,
	FBDHRequestSurveyPage,
	FBDHPipelineListPage,
	FBDHPipelinePage,

	// CCDH
	CCDHProspectListPage,
	CCDHAssignCCAListPage,
	CCDHAssignCCAPage,
	CCDHAnalisaSLIKListPage,
	CCDHAnalisaSLIKPage,
	CCDHSurveyApprovalListPage,
	CCDHSurveyApprovalPages,
	CCDHVisitReportListPage,
	CCDHVisitReportApproval,
	CCDHPipelineListPage,
	CCDHPipelinePage,
	CCDHPipelineSummaryPage,

	CCARequestSLIKPage,
	CCARequestSLIKForm,
	CCAAnalisaSLIKPage,
	CCAAnalisaSLIKForm,
	CCASurveyVerificationListPage,
	CCASurveyVerificationPage,
	CCAPipelineListPage,
	CCAPipelinePage,

	GroupAccessPage,
	GroupAccessForm,
	MasterMenuPage,
	MasterGroupPage,
	GroupMenuPage,
	UserGroupPage,
	AccountPage,

	CCAProspectListPage,

	FHSurveyApprovalListPage,
	FHSurveyApprovalPage,
	CHSurveyApprovalListPage,
	CHSurveyApprovalPage,
	FRMPraSurveyPage,
	// FRMPraSurveyForm,
	FRMRequestSurveyPage,
	FRMRequestSurveyForm,

	FRMVisitReportRejectPage,
	FRMVisitReportListPage,
	FRMVisitReportPage,
	FRMSiteVisitReportPage,
	FRMCallReportListPage,
	FRMCallReportHistoryPage,
	FRMCallReportPage,
	FRMPOListPage,
	FRMPODetailListPage,
	FRMPOActionListPage,
	FCASPOListPage,

	FBDHVisitReportListPage,
	FBDHVisitReportApproval,
	FBDHVisitReportRejectListPage,
	FBDHVisitReportRejectPage,
	FBDHPOListPage,
	FBDHPODetailListPage,
	FBDHPOApprovalListPage,

	FHVisitReportListPage,
	FHVisitReportApproval,
	FHPipelineListPage,

	CHVisitReportListPage,
	CHVisitReportApproval,
	CHPipelineListPage,

	// FRMSurveyPage,
	// FRMSurveyDetailForm,
	// StaticFBDHSurveyPage,
	// StaticFBDHSurveyDetailForm,
	// StaticFRMPraSurveyForm,
	// CCAPraSurveyPage,
	// CCAPraSurveyDetail,
	// CCASurveyPage,
	// CCASurveyDetail,
	// CCDHSurveyApprovalPage,
	// CCDHSurveyApprovalDetail,
	// KadivFleetApprovalPage,
	// KadivFleetApprovalDetail,
	// KadivCreditApprovalPage,
	// KadivCreditApprovalDetail,
	CCAVisitReportListPage,
	CCAVisitReportVerificationPage,

	NAPListPage,
	NAPDownloadPage,
	NAPJFDownloadPage,
	NAPPage,

	GoliveListPage,
	GolivePage,

	FSCustomerListPage,
	FSCustomerPage,

	SCRisalahListPage,
	SCRisalahPage,

	// OCR
	OCRListPage,
	OCRPage,
	OCRPageDetail,
	OCRCreate,

	// SPF
	SPFListPage,
	SPFPage,

	// MKL
	MKLListPage,
	MKLPage,
	MKLHistorySPFPage,

	// Robotic
	QCNewCustomerListPage,
	QCNewCustomerPage,
	QCPraPencairanListPage,
	QCPraPencairanPage,
	ActivityRoboticPraPencairanListPage,
	ActivityRoboticDetailPage,

	// MPP
	MPPListPage,
	MPPPage,
	CreateMPPListPage,
	HistoryMPPListPage,
	HistorySLIKMPPPage,
	HistorySLIKMPPListPage,

	// Print Disbursement
	PrintListPage,
	PrintPage,
	DisbursementListPage,
	DisbursementPOListPage,
	DisbursementPOHistory,
	DisbursementPage,
	VerifyDisbursementListPage,
	VerifyDisbursementPOListPage,
	VerifyDisbursementPage,
	VerificationPOHistory,

	// Monitoring
	MonitoringSLAListPage,
	MonitoringSLAPage,
	MonitoringPlafondListPage,
	MonitoringPlafondPage,
	MonitoringPlafondAgreement,
	MonitoringPlafondPO,
	MonitoringCovenantListPage,
	MonitoringCovenantPage,
	MonitoringCovenantCustomerPage,

	// DMS
	DMSListPage,
	DMSDetailPage,

	// Monitoring Document
	MonitoringDocumentListPage,
	MonitoringDocumentDetailPage,
	MonitoringPipelineGolivePage,

	FRMSimulationListPage,
	FRMSimulationPage,
	SimulationFacilityListPage,
	FRMSimulationHistoryListPage,
	SimulationPage,
	SimulationPagePreview,

	// FCS
	PlakatListPage,
	// Admin Pages
	AdminLoginPage,
	AdminDashboardPage,
	AdminUserListPage,
	AdminEmailLogsPage,
	AdminBouwheerListPage,
	AdminOrgStructurePage,
	AdminIndustrySectorListPage,
	AdminCustomerListPage,
};

export type PageName = keyof typeof PAGE_COMPONENT;

type PageComponent<T extends PageName> = {
	name: T;
	props?: Parameters<(typeof PAGE_COMPONENT)[T]>[0];
};

type PageContext = {
	page: PageComponent<PageName>;
	navigate: <T extends PageName>(
		name: T,
		props?: PageComponent<T>['props'],
	) => void;
};

const pageContext = createContext<PageContext | null>(null);
const { Provider: PageProvider } = pageContext;

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: false,
			refetchOnWindowFocus: false,
		},
	},
});

function FleetApp() {
	const [component, setComponent] = useState(() => {
		const isAuthenticated = Boolean(getCookie('FLTRT'));
		const userRole = localStorage.getItem('role');

		if (!isAuthenticated) {
			// If user has admin role stored but no token, redirect to admin login
			if (userRole === 'Admin') {
				return {
					name: 'AdminLoginPage',
				} as PageComponent<never>;
			}

			return {
				name: 'LoginPage',
			} as PageComponent<never>;
		}

		const pageQuery = new URL(window.location.href).searchParams.get(
			'page',
		) as PageName;

		const pageStorage = JSON.parse(
			sessionStorage.getItem('page') ?? '{}',
		) as PageComponent<never>;

		// If user is admin but trying to access regular login, redirect to admin dashboard
		if (userRole === 'Admin' && pageQuery === 'LoginPage') {
			return {
				name: 'AdminDashboardPage',
			} as PageComponent<never>;
		}

		const initialPage =
			pageStorage.name ??
			(userRole === 'Admin' ? 'AdminDashboardPage' : 'DashboardPage');
		const initialProps = pageStorage.name ? pageStorage.props : {};

		return {
			name: pageQuery in PAGE_COMPONENT ? pageQuery : initialPage,
			props: pageQuery in PAGE_COMPONENT ? {} : initialProps,
		} as PageComponent<never>;
	});

	const navigate: PageContext['navigate'] = useCallback((name, props) => {
		setComponent({ name, props } as PageComponent<never>);
		sessionStorage.setItem('page', JSON.stringify({ name, props }));
		// destructure props so we can add as query string url

		// const queryString = props
		// 	? '&' +
		// 		Object.entries(props!)
		// 			.map(
		// 				([key, value]) =>
		// 					`${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
		// 			)
		// 			.join('&')
		// 	: '';
		// window.history.pushState({}, '', `?page=${name}${queryString}`);
		window.scrollTo({
			top: 0,
			left: 0,
		});
	}, []);

	// useEffect(() => {
	// 	/**
	// 	 * todo: handle url change
	// 	 * when production, detect changes in sessionStorage for key page. we're hidding the url in browser.
	// 	 */
	// 	const handleUrlChange = () => {
	// 		const pageQuery = new URL(window.location.href).searchParams.get(
	// 			'page',
	// 		) as PageName;

	// 		const pageStorage = JSON.parse(
	// 			sessionStorage.getItem('page') ?? '{}',
	// 		) as PageComponent<never>;

	// 		const initialPage = pageStorage.name ?? 'DashboardPage';
	// 		const initialProps = pageStorage.name ? pageStorage.props : {};

	// 		setComponent({
	// 			name: pageQuery in PAGE_COMPONENT ? pageQuery : initialPage,
	// 			props: pageQuery in PAGE_COMPONENT ? {} : initialProps,
	// 		} as PageComponent<never>);
	// 	};

	// 	window.addEventListener('popstate', handleUrlChange);

	// 	return () => {
	// 		window.removeEventListener('popstate', handleUrlChange);
	// 	};
	// }, []);

	const Page = PAGE_COMPONENT[component.name] as (
		props: NonNullable<unknown>,
	) => ReactNode;

	const Layout =
		component.name !== 'LoginPage' &&
			component.name !== 'AdminLoginPage' &&
			component.name !== 'DocPage' &&
			component.name !== 'NAPDownloadPage' &&
			component.name !== 'NAPJFDownloadPage'
			? AppLayout
			: ({ children }: { children: ReactNode }) => children;

	return (
		<ConfigProvider theme={themeConfig}>
			<QueryClientProvider client={queryClient}>
				<PageProvider
					value={{
						page: component,
						navigate,
					}}
				>
					<MapLoader>
						<App>
							<DebugProvider>
								<Layout>
									<Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
										<ModalProvider>
											<Sentry.ErrorBoundary
												fallback={({
													error,
													resetError,
												}: {
													error: unknown;
													resetError: () => void;
												}) => (
													<div
														style={{
															padding: '20px',
															textAlign: 'center',
															minHeight: '400px',
															display: 'flex',
															flexDirection: 'column',
															justifyContent: 'center',
															alignItems: 'center',
														}}
													>
														<h2>Content Error</h2>
														<p>
															Something went wrong with this page. The
															navigation is still available.
														</p>
														<div
															style={{
																marginTop: '15px',
																display: 'flex',
																gap: '10px',
															}}
														>
															<button
																onClick={resetError}
																style={{
																	padding: '8px 16px',
																	backgroundColor: '#1890ff',
																	color: 'white',
																	border: 'none',
																	borderRadius: '4px',
																	cursor: 'pointer',
																}}
															>
																Try Again
															</button>
															<button
																onClick={() => navigate('DashboardPage')}
																style={{
																	padding: '8px 16px',
																	backgroundColor: '#f0f0f0',
																	color: '#333',
																	border: 'none',
																	borderRadius: '4px',
																	cursor: 'pointer',
																}}
															>
																Go to Dashboard
															</button>
														</div>
														{import.meta.env.DEV && (
															<details
																style={{ marginTop: '20px', textAlign: 'left' }}
															>
																<summary>
																	Error Details (Development Only)
																</summary>
																<pre
																	style={{
																		whiteSpace: 'pre-wrap',
																		fontSize: '12px',
																	}}
																>
																	{error instanceof Error
																		? error.toString()
																		: 'Unknown error'}
																</pre>
															</details>
														)}
													</div>
												)}
												beforeCapture={(scope: Scope) => {
													// Add additional context before capturing the error
													scope.setTag('component', 'PageContent');
													scope.setTag('pageName', component.name);
													scope.setLevel('error');
												}}
											>
												<Page {...(component.props! as NonNullable<unknown>)} />
											</Sentry.ErrorBoundary>
										</ModalProvider>
									</Worker>
								</Layout>
							</DebugProvider>
						</App>
					</MapLoader>
				</PageProvider>
			</QueryClientProvider>
		</ConfigProvider>
	);
}

export default FleetApp;

export const useApp = () => {
	const { page, navigate } = useContext(pageContext)!;
	const { message, notification, modal } = App.useApp();

	return {
		page,
		navigate,
		message,
		modal,
		notification,
		queryClient,
	};
};
